import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  Empty,
  Row,
  Col,
  Badge,
  Divider,
  Collapse,
  InputNumber,
  Switch,
  Input,
  Tooltip,
  Modal,
} from "antd";
import {
  TruckOutlined,
  EuroCircleOutlined,
  InfoCircleOutlined,
  ShoppingOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  StarFilled,
} from "@ant-design/icons";
import { sendcloudService } from "../../services/sendcloudService";
import { getEnabledShippingMethodIds } from "../../services/sendcloudShippingMethodSettingsService";
import {
  buildOwnDeliveryShippingOption,
  createOwnDeliveryShipments,
  isOwnDeliveryEnabled,
} from "../../services/ownDeliveryService";
import {
  isOwnDeliveryMethodId,
  OWN_DELIVERY_METHOD_ID,
} from "../../constants/ownDelivery";
import { useOwnDeliveryRecommendation } from "../../hooks/useOwnDeliveryRecommendation";
import { supabase } from "../../lib/supabase";
import type { SendcloudShippingOption } from "../../types/sendcloud";
import { getCountryCode } from "../../utils/countryMapping";
import { isCustomerEtaOverrideBlockingSendcloudLabels } from "../../utils/bookingUtils";
import {
  getSendcloudCustomerEtaOverrideDeadlineYmd,
  todayYmdEuropeAmsterdam,
} from "../../utils/nlPublicHolidays";

// Helper function to parse and format Sendcloud API errors
const parseShippingError = (errorMessage: string): string => {
  try {
    // Check if it's a Sendcloud API error with specific validation messages
    if (errorMessage.includes("Sendcloud API error")) {
      // Extract the JSON error message from the error string
      const jsonMatch = errorMessage.match(/\{.*\}/);
      if (jsonMatch) {
        const errorJson = JSON.parse(jsonMatch[0]);

        if (errorJson.error) {
          const sendcloudError = errorJson.error;

          // Handle specific field validation errors
          if (sendcloudError.message) {
            // Parse field-specific errors like: 'postal_code: "Enter a valid zip code."'
            const fieldErrors =
              sendcloudError.message.match(/(\w+):\s*"([^"]+)"/g);
            if (fieldErrors) {
              const formattedErrors = fieldErrors.map((error: string) => {
                const [, field, message] =
                  error.match(/(\w+):\s*"([^"]+)"/) || [];
                return `${field?.replace("_", " ")}: ${message}`;
              });
              return `Sendcloud validation error: ${formattedErrors.join(
                ", ",
              )}`;
            }

            // Return the raw message if we can't parse it better
            return `Sendcloud error: ${sendcloudError.message}`;
          }

          // Handle other error formats
          if (typeof sendcloudError === "string") {
            return `Sendcloud error: ${sendcloudError}`;
          }
        }
      }

      // If we can't parse the JSON, return a cleaned version
      return errorMessage.replace(
        /^.*Sendcloud API error[^:]*:\s*/,
        "Sendcloud error: ",
      );
    }

    // For non-Sendcloud errors, return as-is
    return errorMessage;
  } catch {
    // If parsing fails, return the original error
    return errorMessage;
  }
};

const { Text } = Typography;

// Air freight constants for calculations
const AIR_FREIGHT = {
  VOLUMETRIC_FACTOR: 167, // 1 CBM = 167 kg for air freight
};

interface Product {
  id?: number;
  name?: string;
  gross_weight: number;
  cbm: number;
  value?: number;
  cartons?: number;
  pieces?: number;
}

interface DeliveryInfo {
  country?: string;
  postal_code?: string;
  city?: string;
  street_name?: string;
  house_number?: string;
  name?: string;
  company_name?: string;
  email?: string;
  telephone?: string;
}

interface CompanyInfo {
  name?: string;
  contact_person?: string;
  email?: string;
  phone_number?: string;
}

interface BookingData {
  totalWeight: number;
  totalVolume: number;
  chargeableWeight: number;
  isAirFreight: boolean;
  products: Product[];
  delivery_info?: DeliveryInfo | DeliveryInfo[];
  company_info?: CompanyInfo | CompanyInfo[];
}

interface ShippingOptionsProps {
  deliveryInfo: {
    id?: number;
    country?: string;
    postal_code?: string;
    city?: string;
    street_name?: string;
    house_number?: string;
    instructions?: string;
  } | null;
  bookingId: number;
  // Override carton/pallet counts from container unloading process
  deliveryCartons?: number;
  deliveryPallets?: number;
  onCreateShipment?: (shippingMethodId: number) => void;
  onShipmentCreated?: () => void;
  disabled?: boolean;
  /** When set and `disabled` is true, used for tooltips and the restriction alert instead of only the booking-status copy. */
  disabledHint?: string;
  /** Customer ETA override on booking (`bookings.eta`); blocks shipment creation when after the holiday-aware Amsterdam deadline. */
  customerEta?: string | null;
}

export const ShippingOptions: React.FC<ShippingOptionsProps> = ({
  deliveryInfo,
  bookingId,
  deliveryCartons,
  deliveryPallets,
  onCreateShipment,
  onShipmentCreated,
  disabled = false,
  disabledHint,
  customerEta,
}) => {
  const [loading, setLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState<
    SendcloudShippingOption[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [dashboardProductTotal, setDashboardProductTotal] = useState<
    number | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [showRequestData, setShowRequestData] = useState(true);
  const [quantityInput, setQuantityInput] = useState<number | null>(1);
  const [showLVBWarning, setShowLVBWarning] = useState(false);
  const [requestData, setRequestData] = useState({
    // Address data (populated from booking/delivery info)
    address: {
      country: "" as string,
      postalCode: "" as string,
      city: "" as string,
      streetName: "" as string,
      houseNumber: "" as string,
      name: "" as string,
      companyName: "" as string,
      email: "" as string,
      telephone: "" as string,
    },
    // Package data (populated from booking products)
    package: {
      weight: 1000 as number, // in grams
      length: 30 as number, // in cm
      width: 20 as number, // in cm
      height: 10 as number, // in cm
      cartons: 1 as number | null,
      value: 0 as number, // insured value
    },
  });

  // Effective address for 20 km Own Delivery recommendation (edited form wins)
  const recommendationAddress = useMemo(
    () => ({
      street_name:
        requestData.address.streetName || deliveryInfo?.street_name || "",
      house_number:
        requestData.address.houseNumber || deliveryInfo?.house_number || "",
      postal_code:
        requestData.address.postalCode || deliveryInfo?.postal_code || "",
      city: requestData.address.city || deliveryInfo?.city || "",
      country: requestData.address.country || deliveryInfo?.country || "",
    }),
    [
      requestData.address.streetName,
      requestData.address.houseNumber,
      requestData.address.postalCode,
      requestData.address.city,
      requestData.address.country,
      deliveryInfo?.street_name,
      deliveryInfo?.house_number,
      deliveryInfo?.postal_code,
      deliveryInfo?.city,
      deliveryInfo?.country,
    ],
  );

  const {
    isRecommended: preferOwnDelivery,
    distanceKm: ownDeliveryDistanceKm,
    loading: ownDeliveryDistanceLoading,
  } = useOwnDeliveryRecommendation(recommendationAddress);

  const ownDeliveryPreferAppliedRef = useRef(false);

  const etaOverrideBlocksShipment = useMemo(
    () => isCustomerEtaOverrideBlockingSendcloudLabels(customerEta ?? null),
    [customerEta],
  );

  // Note: Country mapping is now handled by the centralized countryMapping utility

  // Reset state when bookingId changes (new booking selected)
  useEffect(() => {
    setSelectedOption(null);
    setQuantityInput(1);
    setError(null);
    setShippingOptions([]);
    ownDeliveryPreferAppliedRef.current = false;
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDashboardProductTotal(null);
      const { data: row, error: bookErr } = await supabase
        .from("bookings")
        .select("company_id")
        .eq("id", bookingId)
        .maybeSingle();
      if (bookErr || row?.company_id == null || cancelled) return;
      const { count, error: cntErr } = await supabase
        .from("company_product")
        .select("id", { count: "exact", head: true })
        .eq("company_id", row.company_id);
      if (!cancelled && !cntErr) setDashboardProductTotal(count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  // Populate request data from booking and delivery info
  const populateRequestData = () => {
    try {
      // Use the already loaded booking data
      const currentBookingData = bookingData;

      // Calculate package dimensions from booking data
      let packageWeight = 1000; // default 1kg
      let packageCartons = 1;
      let packageValue = 0;

      if (currentBookingData && currentBookingData.products.length > 0) {
        // Use delivery cartons from container unloading if available, otherwise calculate from products
        packageCartons =
          deliveryCartons ||
          currentBookingData.products.reduce(
            (sum: number, product: Product) => sum + (product.cartons || 1),
            0,
          );

        // Calculate weight per carton (not total weight!)
        const totalWeightKg = currentBookingData.totalWeight;
        const weightPerCartonKg = totalWeightKg / Math.max(packageCartons, 1);
        packageWeight = Math.round(weightPerCartonKg * 1000); // Convert to grams

        // Always set insured value to 0
        packageValue = 0;
      }

      console.log(
        `Populating request data with weight per carton: ${packageWeight}g (${(
          packageWeight / 1000
        ).toFixed(2)}kg per carton) from ${
          currentBookingData?.products.length || 0
        } products, ${packageCartons} total cartons ${
          deliveryCartons
            ? "(from container unloading)"
            : "(from product estimates)"
        }`,
      );

      // Calculate realistic dimensions from CBM
      const totalVolume = currentBookingData?.totalVolume || 0.006; // default 6 liters
      const estimatedLength = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 1.5),
        200,
      );
      const estimatedWidth = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 1.2),
        120,
      );
      const estimatedHeight = Math.min(
        Math.round(Math.cbrt(totalVolume * 1000000) * 0.8),
        100,
      );

      // Get delivery address from booking data - these come as arrays from Supabase relations
      const deliveryData = Array.isArray(currentBookingData?.delivery_info)
        ? currentBookingData.delivery_info[0]
        : currentBookingData?.delivery_info;
      const companyData = Array.isArray(currentBookingData?.company_info)
        ? currentBookingData.company_info[0]
        : currentBookingData?.company_info;

      console.log("Delivery data (after array access):", deliveryData);
      console.log("Company data (after array access):", companyData);
      console.log("Raw booking data:", currentBookingData);

      // Prefill only from delivery / company. User-login emails are resolved
      // server-side in sendcloud-shipment (auth_admin, then safe_users fallback).
      const resolvedEmail =
        deliveryData?.email || companyData?.email || "";

      console.log("Email resolution in frontend:", {
        deliveryEmail: deliveryData?.email || "none",
        companyEmail: companyData?.email || "none",
        finalEmail: resolvedEmail || "none (edge will resolve)",
      });

      setRequestData({
        address: {
          country: deliveryData?.country || deliveryInfo?.country || "",
          postalCode:
            deliveryData?.postal_code || deliveryInfo?.postal_code || "",
          city: deliveryData?.city || deliveryInfo?.city || "",
          streetName:
            deliveryData?.street_name || deliveryInfo?.street_name || "",
          houseNumber:
            deliveryData?.house_number || deliveryInfo?.house_number || "",
          name:
            deliveryData?.name ||
            companyData?.contact_person ||
            companyData?.name ||
            "",
          companyName: deliveryData?.company_name || companyData?.name || "",
          email: resolvedEmail,
          telephone: deliveryData?.telephone || companyData?.phone_number || "",
        },
        package: {
          weight: packageWeight,
          length: estimatedLength,
          width: estimatedWidth,
          height: estimatedHeight,
          cartons: packageCartons,
          value: packageValue,
        },
      });
    } catch (error) {
      console.error("Error populating request data:", error);
    }
  };

  const fetchBookingData = async () => {
    try {
      const { data: booking, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          booking_transport_types!transport_type(id, name, abbreviation),
          booking_product!booking_id(
            id,
            name,
            gross_weight,
            cbm,
            value,
            cartons,
            pieces
          ),
          booking_delivery!booking_id(*),
          company!company_id(
            *
          )
        `,
        )
        .eq("id", bookingId)
        .single();

      if (error || !booking) {
        console.error("Failed to fetch booking data:", error);
        return null;
      }

      // Get products array
      const products = Array.isArray(booking.booking_product)
        ? booking.booking_product
        : [];

      // Calculate totals
      const totalWeight = products.reduce(
        (sum: number, product: Product) => sum + (product.gross_weight || 0),
        0,
      );
      const totalVolume = products.reduce(
        (sum: number, product: Product) => sum + (product.cbm || 0),
        0,
      );

      // Check if air freight
      const transportTypes = Array.isArray(booking.booking_transport_types)
        ? booking.booking_transport_types
        : [booking.booking_transport_types].filter(Boolean);
      const isAirFreight = transportTypes[0]?.id === 1;

      // Calculate chargeable weight (for air freight, use higher of actual or volumetric)
      const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;
      const chargeableWeight = isAirFreight
        ? Math.max(totalWeight, volumetricWeight)
        : totalWeight;

      const bookingData = {
        totalWeight,
        totalVolume,
        chargeableWeight,
        isAirFreight,
        products,
        delivery_info: Array.isArray(booking.booking_delivery)
          ? booking.booking_delivery
          : [booking.booking_delivery].filter(Boolean),
        company_info: Array.isArray(booking.company)
          ? booking.company
          : [booking.company].filter(Boolean),
      };

      setBookingData(bookingData);
      return bookingData;
    } catch (err) {
      console.error("Error fetching booking data:", err);
      return null;
    }
  };

  const fetchShippingOptions = async () => {
    if (!deliveryInfo?.country || !deliveryInfo?.postal_code) {
      setError("Delivery address is required to fetch shipping options");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const countryCode = getCountryCode(deliveryInfo.country);

      // Always use 1kg (1000g) for fetching available options
      const weightInGrams = 1000;

      const finalCountry = requestData.address.country || countryCode;
      const finalPostalCode =
        requestData.address.postalCode || deliveryInfo.postal_code;

      const requestParams = {
        country: finalCountry,
        postalCode: finalPostalCode,
        weight: weightInGrams,
      };

      const [response, allowedShippingMethodIds, ownDeliveryEnabled] =
        await Promise.all([
          sendcloudService.getShippingOptions(requestParams),
          getEnabledShippingMethodIds(countryCode),
          isOwnDeliveryEnabled(),
        ]);

      if (response.success && response.data) {
        const allShippingMethods = response.data.shipping_methods || [];

        const filteredMethods = allShippingMethods.filter(
          (method) =>
            method.countries &&
            method.countries.some(
              (country) =>
                country.iso_2.toLowerCase() === countryCode.toLowerCase(),
            ),
        );

        const targetCountryMethods = filteredMethods.map((method) => ({
          ...method,
          countries: method.countries.filter(
            (country) =>
              country.iso_2.toLowerCase() === countryCode.toLowerCase(),
          ),
        }));

        const finalFilteredMethods = targetCountryMethods.filter((method) =>
          allowedShippingMethodIds.includes(method.id),
        );

        const withOwnDelivery = [...finalFilteredMethods];
        if (ownDeliveryEnabled) {
          withOwnDelivery.push(buildOwnDeliveryShippingOption(countryCode));
        }

        setShippingOptions(withOwnDelivery);
      } else {
        const errorMessage = parseShippingError(
          response.error || "Failed to fetch shipping options",
        );
        if (ownDeliveryEnabled) {
          setShippingOptions([buildOwnDeliveryShippingOption(countryCode)]);
          setError(null);
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      const ownDeliveryEnabled = await isOwnDeliveryEnabled();
      if (ownDeliveryEnabled) {
        setShippingOptions([
          buildOwnDeliveryShippingOption(
            getCountryCode(deliveryInfo?.country || "NL"),
          ),
        ]);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch booking data when component mounts or bookingId changes
  useEffect(() => {
    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId]);

  // Populate request data when booking data becomes available
  useEffect(() => {
    console.log("useEffect populateRequestData triggered with:", {
      deliveryInfo,
      bookingId,
      bookingData: !!bookingData,
      hasCountry: !!deliveryInfo?.country,
      hasPostalCode: !!deliveryInfo?.postal_code,
    });

    if (bookingData && deliveryInfo) {
      console.log("Calling populateRequestData...");
      populateRequestData();
    } else {
      console.log("Not calling populateRequestData - missing required data");
    }
  }, [bookingData, deliveryInfo]);

  // Fetch shipping options as soon as address is available (no need to wait for booking products)
  useEffect(() => {
    if (deliveryInfo?.country && deliveryInfo?.postal_code) {
      fetchShippingOptions();
    }
  }, [deliveryInfo?.country, deliveryInfo?.postal_code, bookingId]);

  // Prefer Own Delivery when within recommend radius (warehouse → address ≤ 20 km)
  useEffect(() => {
    if (!preferOwnDelivery || shippingOptions.length === 0) return;
    if (ownDeliveryPreferAppliedRef.current) return;
    const hasOwnDelivery = shippingOptions.some((o) =>
      isOwnDeliveryMethodId(o.id),
    );
    if (!hasOwnDelivery) return;

    setSelectedOption((prev) => {
      if (prev != null) return prev;
      ownDeliveryPreferAppliedRef.current = true;
      setQuantityInput(deliveryPallets || 1);
      return OWN_DELIVERY_METHOD_ID;
    });
  }, [preferOwnDelivery, shippingOptions, deliveryPallets]);

  // Check if the delivery address is an LVB address (case-insensitive)
  const isLVBAddress = (): boolean => {
    if (!deliveryInfo?.street_name) return false;

    const streetName = deliveryInfo.street_name.toLowerCase().trim();
    const lvbAddresses = ["mechie trommelenweg", "veerweg", "divisiedok"];

    return lvbAddresses.some((lvbAddress) =>
      streetName.includes(lvbAddress.toLowerCase()),
    );
  };

  const getEffectiveDeliveryAddress = () => ({
    streetName:
      requestData.address.streetName || deliveryInfo?.street_name || "",
    houseNumber:
      requestData.address.houseNumber || deliveryInfo?.house_number || "",
    city: requestData.address.city || deliveryInfo?.city || "",
    postalCode:
      requestData.address.postalCode || deliveryInfo?.postal_code || "",
    country: requestData.address.country || deliveryInfo?.country || "",
  });

  // TRL warehouse: Barnsteen 800, 3316 KK Dordrecht
  const isTRLAddress = (): boolean => {
    const { streetName, houseNumber, city, postalCode, country } =
      getEffectiveDeliveryAddress();

    const normalizedStreet = streetName.toLowerCase().trim();
    const normalizedCity = city.toLowerCase().trim();
    const normalizedPostalCode = postalCode.toUpperCase().trim().replace(/\s+/g, "");
    const normalizedCountry = country.toLowerCase().trim();
    const normalizedHouseNumber = houseNumber.trim();

    const matchesCountry =
      normalizedCountry === "" ||
      normalizedCountry === "nederland" ||
      normalizedCountry === "netherlands" ||
      normalizedCountry === "nl";

    const matchesHouseNumber =
      normalizedHouseNumber === "" ||
      normalizedHouseNumber === "800" ||
      normalizedStreet.includes("800");

    return (
      normalizedStreet.includes("barnsteen") &&
      normalizedCity === "dordrecht" &&
      normalizedPostalCode.startsWith("3316") &&
      matchesCountry &&
      matchesHouseNumber
    );
  };

  // Check if the delivery address is the Amersfoort address that should be disabled
  const isAmersfoortDisabledAddress = (): boolean => {
    if (!deliveryInfo) return false;

    const streetName = deliveryInfo.street_name?.toLowerCase().trim() || "";
    const city = deliveryInfo.city?.toLowerCase().trim() || "";
    // Normalize postal code by removing spaces for comparison
    const postalCode = (
      deliveryInfo.postal_code?.toUpperCase().trim() || ""
    ).replace(/\s+/g, "");
    const country = deliveryInfo.country?.toLowerCase().trim() || "";

    // Check if it matches: Lindeboomseweg (with or without house number), Amersfoort, 3825AL (with or without space), Nederland
    return (
      streetName.includes("lindeboomseweg") &&
      city === "amersfoort" &&
      postalCode === "3825AL" &&
      (country === "nederland" || country === "netherlands")
    );
  };

  // Actually create the shipment (extracted from handleCreateShipment)
  const createShipmentInternal = async () => {
    if (!selectedOption || !onCreateShipment) return;

    try {
      setLoading(true);
      setError(null);

      if (etaOverrideBlocksShipment && !isOwnDeliveryMethodId(selectedOption)) {
        const d = getSendcloudCustomerEtaOverrideDeadlineYmd(
          todayYmdEuropeAmsterdam(),
        );
        setError(
          `Geen Sendcloud-zending: de Customer ETA Override ligt na de label-deadline (${d}, Amsterdam incl. feestdagen). Pas de override aan of wacht.`,
        );
        return;
      }

      // E-Freight own delivery — local shipments + labels, no Sendcloud
      if (isOwnDeliveryMethodId(selectedOption)) {
        const palletCount = quantityInput || deliveryPallets || 1;
        const response = await createOwnDeliveryShipments({
          bookingId: bookingId,
          palletCount,
          addressOverrides: Object.keys(requestData.address).some(
            (key) =>
              requestData.address[key as keyof typeof requestData.address],
          )
            ? requestData.address
            : undefined,
        });

        if (response.success) {
          onCreateShipment(selectedOption);
          onShipmentCreated?.();
          console.log("Own delivery shipments created:", response.data);
        } else {
          setError(response.error || "Failed to create own delivery shipment");
        }
        return;
      }

      // For pallet options (25013, 53), do not include dimensions
      const shouldIncludeDimensions =
        selectedOption !== 25013 && selectedOption !== 53;
      const isPalletShipment =
        selectedOption === 25013 || selectedOption === 53;

      // Calculate actual weight from booking data (non-pallet only — backend divides total weight by pallet count)
      const currentBookingData = bookingData;
      let actualWeightInGrams: number | undefined;

      if (currentBookingData && !isPalletShipment) {
        // For non-pallet shipments, calculate per carton as before
        const totalCartons =
          deliveryCartons ||
          currentBookingData.products.reduce(
            (sum: number, product: Product) => sum + (product.cartons || 1),
            0,
          );

        // Calculate weight per carton
        const weightPerCartonKg =
          currentBookingData.totalWeight / Math.max(totalCartons, 1);
        actualWeightInGrams = Math.round(weightPerCartonKg * 1000);

        console.log(
          `Calculating carton weight: ${currentBookingData.totalWeight}kg total ÷ ${totalCartons} cartons = ${weightPerCartonKg}kg per carton (${actualWeightInGrams}g)`,
        );
      }

      const palletCount =
        quantityInput ?? deliveryPallets ?? 1;

      if (isPalletShipment) {
        console.log(
          `Pallet shipment: ${palletCount} pallets, total weight ${currentBookingData?.totalWeight ?? "?"}kg (backend calculates per-pallet weight)`,
        );
      }

      // Use the actual weight for shipment creation
      const response = await sendcloudService.createShipment({
        bookingId: bookingId,
        shippingMethodId: selectedOption,
        parcelData: {
          ...(actualWeightInGrams !== undefined && { weight: actualWeightInGrams }),
          // Only include dimensions for non-pallet shipping methods
          ...(shouldIncludeDimensions && {
            length: requestData.package.length,
            width: requestData.package.width,
            height: requestData.package.height,
          }),
          cartons: getQuantityInfo(selectedOption)
            ? palletCount
            : undefined,
          ...(isPalletShipment && { pallets: palletCount }),
        },
        addressOverrides: Object.keys(requestData.address).some(
          (key) => requestData.address[key as keyof typeof requestData.address],
        )
          ? requestData.address
          : undefined,
      });

      if (response.success) {
        // Call the parent callback with the created shipment data
        onCreateShipment(selectedOption);

        // Notify parent that shipment was created successfully
        onShipmentCreated?.();

        // Show success message or redirect
        console.log("Shipment created successfully:", response.data);
      } else {
        // Parse and display better error messages
        const errorMessage = parseShippingError(
          response.error || "Failed to create shipment",
        );
        setError(errorMessage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Handle create shipment button click - check for special delivery addresses first
  const showTRLConfirmationModal = () => {
    Modal.confirm({
      title: "Let op: TRL pallet hoogte",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>
            TRL leveringsadres gedetecteerd
          </p>
          <p>
            TRL accepteert alleen europallets van maximaal{" "}
            <strong>140 cm</strong> hoog.
          </p>
          <p style={{ marginTop: 12 }}>
            Controleer vóór het aanmaken van het verzendlabel dat de pallets
            niet hoger gestapeld zijn. Leveringen boven 140 cm worden
            geweigerd.
          </p>
          <p style={{ marginTop: 12, fontWeight: "bold" }}>
            Wilt u toch doorgaan met het aanmaken van de verzending?
          </p>
        </div>
      ),
      okText: "Create Shipment with Selected Option",
      cancelText: "Annuleren",
      okButtonProps: { danger: true },
      width: 600,
      centered: true,
      onOk: () => createShipmentInternal(),
    });
  };

  const handleCreateShipment = () => {
    if (!selectedOption || !onCreateShipment) return;

    if (isLVBAddress()) {
      setShowLVBWarning(true);
      return;
    }

    if (isTRLAddress()) {
      showTRLConfirmationModal();
      return;
    }

    createShipmentInternal();
  };

  const formatWeight = (weight: string | number) => {
    const numWeight = typeof weight === "string" ? parseFloat(weight) : weight;
    if (numWeight < 1) {
      return `${(numWeight * 1000).toFixed(0)}g`;
    }
    return `${numWeight.toFixed(1)}kg`;
  };

  // Helper function to determine quantity type and default value
  const getCarrierColor = (carrier: string) => {
    const colors: Record<string, string> = {
      postnl: "orange",
      dhl: "red",
      dpd: "red",
      ups: "brown",
      fedex: "purple",
      gls: "blue",
      bpost: "yellow",
      chronopost: "red",
      efreight: "green",
    };
    return colors[carrier.toLowerCase()] || "default";
  };

  const getQuantityInfo = (shippingOptionId: number | null) => {
    if (!shippingOptionId) return null;

    // Use delivery cartons/pallets from container unloading if available, otherwise fallback to product estimates
    const totalCartons =
      deliveryCartons ||
      bookingData?.products?.reduce(
        (sum, product) => sum + (product.cartons ?? 0),
        0,
      ) ||
      1;

    const totalPallets = deliveryPallets || 1;

    if (
      shippingOptionId === OWN_DELIVERY_METHOD_ID ||
      shippingOptionId === 25013 ||
      shippingOptionId === 53
    ) {
      // Pallet shipping - use delivery pallets from container unloading
      return {
        type: "pallets",
        label: "Number of Pallets",
        defaultValue: totalPallets,
        shouldPrefill: true,
      };
    } else if (
      shippingOptionId === 8229 ||
      shippingOptionId === 3366 ||
      shippingOptionId === 68 ||
      shippingOptionId === 16
    ) {
      // Carton shipping - use delivery cartons from container unloading, but max 7
      // This applies to GLS EconomyParcel (8229), Belgium-specific option (3366), additional GLS method (68), and Parcel/Boxes shipment (16)
      return {
        type: "cartons",
        label: "Number of Cartons",
        defaultValue: Math.min(totalCartons, 7),
        shouldPrefill: true,
      };
    }

    return null;
  };

  /** Carton vs pallet group for display; falls back to name when ID isn't mapped. */
  const getOptionGroup = (
    option: SendcloudShippingOption,
  ): "cartons" | "pallets" => {
    const info = getQuantityInfo(option.id);
    if (info?.type === "pallets") return "pallets";
    if (info?.type === "cartons") return "cartons";
    if (/pallet/i.test(option.name)) return "pallets";
    return "cartons";
  };

  // Handle option selection and set quantity
  const handleOptionSelection = (optionId: number) => {
    setSelectedOption(optionId);

    const quantityInfo = getQuantityInfo(optionId);
    if (quantityInfo) {
      setQuantityInput(
        quantityInfo.shouldPrefill ? quantityInfo.defaultValue : 1,
      );
    } else {
      setQuantityInput(1);
    }

    // Auto-scroll to shipment creation section on mobile
    setTimeout(() => {
      const shipmentSection = document.querySelector(
        ".shipment-creation-section",
      );
      if (shipmentSection) {
        shipmentSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const sendcloudIntegrationSubtitle =
    dashboardProductTotal != null ? (
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">Total my products (dashboard): </Text>
        <Text strong>{dashboardProductTotal}</Text>
        <Text type="secondary">
          {" "}
          product{dashboardProductTotal !== 1 ? "s" : ""}
        </Text>
      </div>
    ) : null;

  if (!deliveryInfo) {
    return (
      <Card title="Sendcloud / shipping">
        {sendcloudIntegrationSubtitle}
        <Alert
          type="info"
          message="No Delivery Address"
          description="Please add a delivery address to view available shipping options."
          showIcon
          icon={<TruckOutlined />}
        />
      </Card>
    );
  }

  if (!deliveryInfo.country || !deliveryInfo.postal_code) {
    return (
      <Card title="Sendcloud / shipping">
        {sendcloudIntegrationSubtitle}
        <Alert
          type="warning"
          message="Incomplete Address"
          description="Country and postal code are required to fetch shipping options."
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <TruckOutlined style={{ color: disabled ? "#faad14" : undefined }} />
          <span style={{ color: disabled ? "#faad14" : undefined }}>
            {disabled
              ? "Shipping Options (Restricted)"
              : "Available Shipping Options"}
          </span>
          <Badge
            count={shippingOptions.length}
            color={disabled ? "orange" : "blue"}
          />
        </Space>
      }
      extra={
        <Button
          onClick={fetchShippingOptions}
          loading={loading}
          disabled={disabled}
          size="small"
        >
          Refresh
        </Button>
      }
    >
      {sendcloudIntegrationSubtitle}
      {etaOverrideBlocksShipment && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Geen Sendcloud-zending bij deze klant-ETA"
          description={
            <span>
              De Customer ETA Override ligt na de label-deadline (
              {getSendcloudCustomerEtaOverrideDeadlineYmd(
                todayYmdEuropeAmsterdam(),
              )}
              , Amsterdam incl. NL-feestdagen en weekenden). Pas de override aan
              op het tabblad Algemeen, of wacht tot de datum binnen die deadline
              valt. Own Delivery (eigen levering) blijft wel beschikbaar.
            </span>
          }
        />
      )}
      {/* Status indicator - only show when shipments are restricted */}
      {disabled && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Shipment Creation Restricted"
          description={
            disabledHint ? (
              <div style={{ whiteSpace: "pre-wrap" }}>{disabledHint}</div>
            ) : (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>
                    Shipments can only be created when the booking status is:
                  </strong>
                </div>
                <div>
                  <Tag color="green" style={{ margin: "2px" }}>
                    customs_cleared
                  </Tag>
                  <Tag color="green" style={{ margin: "2px" }}>
                    delivery_planned
                  </Tag>
                  <Tag color="green" style={{ margin: "2px" }}>
                    out_for_delivery
                  </Tag>
                  <Tag color="green" style={{ margin: "2px" }}>
                    delivered
                  </Tag>
                  <Tag color="green" style={{ margin: "2px" }}>
                    added_to_stock
                  </Tag>
                </div>
                <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                  ℹ️ This ensures shipments are only created after customs
                  clearance and proper processing.
                </div>
              </div>
            )
          }
          style={{ marginBottom: 16 }}
          closable={false}
        />
      )}

      {isTRLAddress() && (
        <Alert
          message="TRL leveringsadres gedetecteerd"
          description={
            <>
              TRL accepteert alleen europallets van maximaal{" "}
              <strong>140 cm</strong> hoog. Controleer dat de pallets niet hoger
              gestapeld zijn — leveringen boven 140 cm worden geweigerd.
            </>
          }
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          closable={false}
        />
      )}

      {/* Request Data Editor */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <FilterOutlined />
            <span>Request Data (Edit as needed)</span>
          </Space>
        }
        extra={
          <Switch
            checkedChildren="Show"
            unCheckedChildren="Hide"
            checked={showRequestData}
            onChange={setShowRequestData}
            size="small"
          />
        }
      >
        {showRequestData && (
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: "address",
                label: "Delivery Address",
                children: (
                  <>
                    {isTRLAddress() && (
                      <Alert
                        message="TRL leveringsadres gedetecteerd"
                        description={
                          <>
                            TRL accepteert alleen europallets van maximaal{" "}
                            <strong>140 cm</strong> hoog. Controleer dat de
                            pallets niet hoger gestapeld zijn — leveringen boven
                            140 cm worden geweigerd.
                          </>
                        }
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginBottom: 16 }}
                        closable={false}
                      />
                    )}
                    <Row gutter={[16, 16]}>
                    {/* Address Form - Left Side */}
                    <Col span={16}>
                      <Row gutter={[12, 12]}>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Country:
                            </Typography.Text>
                            <Input
                              placeholder="Country"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.country}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    country: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Postal Code:
                            </Typography.Text>
                            <Input
                              placeholder="Postal code"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.postalCode}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    postalCode: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={8}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              City:
                            </Typography.Text>
                            <Input
                              placeholder="City"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.city}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    city: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Street Name:
                            </Typography.Text>
                            <Input
                              placeholder="Street name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.streetName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    streetName: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              House Number:
                            </Typography.Text>
                            <Input
                              placeholder="House number"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.houseNumber}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    houseNumber: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Contact Name:
                            </Typography.Text>
                            <Input
                              placeholder="Contact name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.name}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    name: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Company Name:
                            </Typography.Text>
                            <Input
                              placeholder="Company name"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.companyName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    companyName: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Email:
                            </Typography.Text>
                            <Input
                              placeholder="Email"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.email}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    email: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              Telephone:
                            </Typography.Text>
                            <Input
                              placeholder="Telephone"
                              style={{ width: "100%", marginTop: 4 }}
                              size="small"
                              value={requestData.address.telephone}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setRequestData((prev) => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    telephone: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>

                    {/* Booking Products - Right Side */}
                    <Col span={8}>
                      <div
                        style={{
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                          padding: "12px",
                          backgroundColor: "#fafafa",
                          height: "fit-content",
                        }}
                      >
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Booking Products ({bookingData?.products?.length || 0}
                          )
                        </Typography.Text>
                        <div
                          style={{
                            marginTop: 8,
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {bookingData?.products &&
                          bookingData.products.length > 0 ? (
                            bookingData.products.map((product, index) => (
                              <div
                                key={product.id || index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "6px 8px",
                                  marginBottom: "4px",
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                  border: "1px solid #e8e8e8",
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Typography.Text
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {product.name || `Product ${index + 1}`}
                                  </Typography.Text>
                                  <Typography.Text
                                    type="secondary"
                                    style={{ fontSize: 10 }}
                                  >
                                    {product.gross_weight
                                      ? `${product.gross_weight}kg`
                                      : "No weight"}
                                  </Typography.Text>
                                </div>
                                <div style={{ marginLeft: 8 }}>
                                  <Tag
                                    color="blue"
                                    style={{ fontSize: 10, margin: 0 }}
                                  >
                                    {product.cartons || 1} carton
                                    {(product.cartons || 1) !== 1 ? "s" : ""}
                                  </Tag>
                                </div>
                              </div>
                            ))
                          ) : (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 11, fontStyle: "italic" }}
                            >
                              No products found
                            </Typography.Text>
                          )}

                          {/* Total Summary */}
                          {bookingData?.products &&
                            bookingData.products.length > 0 && (
                              <div
                                style={{
                                  marginTop: 8,
                                  paddingTop: 8,
                                  borderTop: "1px solid #e8e8e8",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography.Text
                                  strong
                                  style={{ fontSize: 11 }}
                                >
                                  Total:
                                </Typography.Text>
                                <Tag
                                  color={deliveryCartons ? "green" : "orange"}
                                  style={{ fontSize: 10, margin: 0 }}
                                >
                                  {deliveryCartons ||
                                    bookingData.products.reduce(
                                      (sum, product) =>
                                        sum + (product.cartons || 1),
                                      0,
                                    )}{" "}
                                  cartons
                                  {deliveryCartons
                                    ? " (actual from unloading)"
                                    : " (estimated)"}
                                </Tag>
                              </div>
                            )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  </>
                ),
              },
              {
                key: "package",
                label: "Package Details",
                children: (
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Weight (grams):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Weight in grams"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={999999000}
                          value={requestData.package.weight}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                weight: value || 1000,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Length (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Length in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={200}
                          value={requestData.package.length}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                length: value || 30,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Width (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Width in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={120}
                          value={requestData.package.width}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                width: value || 20,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Height (cm):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Height in cm"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={100}
                          value={requestData.package.height}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                height: value || 10,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Cartons:
                        </Typography.Text>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#8c8c8c",
                            marginBottom: 2,
                          }}
                        >
                          {deliveryCartons
                            ? "(actual from container unloading)"
                            : "(sum of all products)"}
                        </div>
                        <InputNumber
                          placeholder="Number of cartons"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={1}
                          max={100}
                          value={requestData.package.cartons}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                cartons: value,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          Insured Value (€):
                        </Typography.Text>
                        <InputNumber
                          placeholder="Insured value"
                          style={{ width: "100%", marginTop: 4 }}
                          size="small"
                          min={0}
                          step={0.01}
                          precision={2}
                          value={requestData.package.value}
                          onChange={(value) =>
                            setRequestData((prev) => ({
                              ...prev,
                              package: {
                                ...prev.package,
                                value: value || 0,
                              },
                            }))
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        )}
      </Card>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Fetching available shipping options...</Text>
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={
            error.includes("validation error")
              ? "Address Validation Error"
              : error.includes("create shipment")
                ? "Shipment Creation Failed"
                : "Shipping Service Error"
          }
          description={
            <div>
              <div style={{ marginBottom: 8 }}>{error}</div>
              {error.toLowerCase().includes("postal") && (
                <div style={{ fontSize: 12, color: "#666" }}>
                  💡 <strong>Tip:</strong> Please check that the postal code
                  format is correct for{" "}
                  {deliveryInfo?.country || "the destination country"}.
                </div>
              )}
              {error.toLowerCase().includes("address") && (
                <div style={{ fontSize: 12, color: "#666" }}>
                  💡 <strong>Tip:</strong> Please verify all address fields are
                  filled in correctly.
                </div>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setError(null);
                fetchShippingOptions();
              }}
            >
              Retry
            </Button>
          }
        />
      )}

      {isAmersfoortDisabledAddress() && (
        <Alert
          message="Let op: Dit is een levering naar ons eigen warenhuis."
          description="SendCloud verzendingen kunnen niet worden aangemaakt voor dit adres (Lindeboomseweg 57, Amersfoort, 3825 AL)."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && !error && shippingOptions.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No shipping options available for this destination"
        />
      )}

      {!loading && !error && shippingOptions.length > 0 && (
        <>
          {(() => {
            const sortByPrice = (
              a: SendcloudShippingOption,
              b: SendcloudShippingOption,
            ) => {
              const priceA =
                a.countries && a.countries[0] ? a.countries[0].price : Infinity;
              const priceB =
                b.countries && b.countries[0] ? b.countries[0].price : Infinity;
              return priceA - priceB;
            };

            const sortPalletOptions = (
              a: SendcloudShippingOption,
              b: SendcloudShippingOption,
            ) => {
              if (preferOwnDelivery) {
                const aOwn = isOwnDeliveryMethodId(a.id);
                const bOwn = isOwnDeliveryMethodId(b.id);
                if (aOwn && !bOwn) return -1;
                if (!aOwn && bOwn) return 1;
              }
              return sortByPrice(a, b);
            };

            const cartonOptions = shippingOptions
              .filter((o) => getOptionGroup(o) === "cartons")
              .sort(sortByPrice);
            const palletOptions = shippingOptions
              .filter((o) => getOptionGroup(o) === "pallets")
              .sort(sortPalletOptions);

            const renderOption = (option: SendcloudShippingOption) => {
              const isOwnDelivery = isOwnDeliveryMethodId(option.id);
              const showRecommended = preferOwnDelivery && isOwnDelivery;

              return (
              <Col span={8} key={option.id} style={{ marginBottom: 16 }}>
                <Card
                  size="small"
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    border:
                      selectedOption === option.id
                        ? "2px solid #1890ff"
                        : showRecommended
                          ? "2px solid #52c41a"
                          : "1px solid #f0f0f0",
                    borderRadius: 8,
                    backgroundColor: showRecommended
                      ? "#f6ffed"
                      : selectedOption === option.id
                        ? "#f6ffed"
                        : "white",
                    height: "100%",
                    boxShadow: showRecommended
                      ? "0 2px 8px rgba(82, 196, 26, 0.25)"
                      : undefined,
                  }}
                  styles={{ body: { padding: 12 } }}
                  onClick={() => handleOptionSelection(option.id)}
                >
                  {showRecommended && (
                    <StarFilled
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 10,
                        fontSize: 36,
                        color: "#faad14",
                        lineHeight: 1,
                        zIndex: 1,
                      }}
                    />
                  )}
                  <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <TruckOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                    <br />
                    <div style={{ margin: "4px 0 0 0" }}>
                      <img
                        src={
                          option.carrier.toLowerCase() === "efreight"
                            ? "/logo_black.png"
                            : `https://cdn.sendcloud.com/global-media/${option.carrier.toLowerCase()}/img/logo.svg`
                        }
                        alt={option.carrier}
                        style={{
                          height: "16px",
                          maxWidth: "40px",
                          objectFit: "contain",
                          marginBottom: 4,
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <br />
                      <Tag
                        color={getCarrierColor(option.carrier)}
                        style={{ fontSize: "10px" }}
                      >
                        {option.carrier.toUpperCase()}
                      </Tag>
                    </div>
                  </div>

                  {showRecommended && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        marginBottom: 8,
                      }}
                    >
                      <Tag
                        color="green"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          margin: 0,
                          padding: "2px 8px",
                        }}
                      >
                        Recommended
                      </Tag>
                    </div>
                  )}

                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        {option.name}
                      </Text>
                      <Space>
                        {option.countries &&
                          option.countries.length > 0 &&
                          option.countries[0].price > 0 && (
                            <Tag color="green" icon={<EuroCircleOutlined />}>
                              €{option.countries[0].price.toFixed(2)}
                            </Tag>
                          )}
                      </Space>
                    </div>
                    {isOwnDelivery && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {ownDeliveryDistanceLoading
                          ? "Calculating drive distance…"
                          : ownDeliveryDistanceKm != null
                            ? `${Math.round(ownDeliveryDistanceKm)} km drive from warehouse`
                            : "Drive distance unavailable"}
                      </Text>
                    )}
                  </div>

                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <div>
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          Weight Range:
                        </Text>
                        <br />
                        <Text style={{ fontSize: "11px" }}>
                          {formatWeight(option.min_weight)} -{" "}
                          {formatWeight(option.max_weight)}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text type="secondary" style={{ fontSize: "11px" }}>
                          Delivery:
                        </Text>
                        <br />
                        <Text style={{ fontSize: "11px" }}>
                          {option.countries &&
                          option.countries[0]?.lead_time_hours
                            ? `${option.countries[0].lead_time_hours}h`
                            : "N/A"}
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Tag
                        color={
                          option.service_point_input === "required"
                            ? "orange"
                            : option.service_point_input === "optional"
                              ? "blue"
                              : "green"
                        }
                        style={{ fontSize: "10px" }}
                      >
                        {option.service_point_input === "required"
                          ? "Service Point"
                          : option.service_point_input === "optional"
                            ? "Optional Point"
                            : "Home Delivery"}
                      </Tag>
                    </Col>
                  </Row>
                </Card>
              </Col>
              );
            };

            const renderGroup = (
              title: string,
              options: SendcloudShippingOption[],
            ) => {
              if (options.length === 0) return null;
              return (
                <div key={title} style={{ marginBottom: 8 }}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      fontSize: 13,
                      marginBottom: 12,
                      color: "rgba(0, 0, 0, 0.65)",
                    }}
                  >
                    {title}
                  </Text>
                  <Row gutter={[16, 16]}>{options.map(renderOption)}</Row>
                </div>
              );
            };

            return (
              <>
                {renderGroup("Cartons", cartonOptions)}
                {cartonOptions.length > 0 && palletOptions.length > 0 && (
                  <Divider style={{ margin: "8px 0 16px" }} />
                )}
                {renderGroup("Pallets", palletOptions)}
              </>
            );
          })()}

          {selectedOption && (
            <>
              <Divider />

              {/* Selected Shipping Option Indicator */}
              <div
                className="shipment-creation-section"
                style={{ marginBottom: 16 }}
              >
                {(() => {
                  const option = shippingOptions.find(
                    (opt) => opt.id === selectedOption,
                  );
                  if (!option) return null;
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: "8px 12px",
                        backgroundColor: "#f0f5ff",
                        borderRadius: 6,
                        marginBottom: 12,
                      }}
                    >
                      <img
                        src={
                          option.carrier.toLowerCase() === "efreight"
                            ? "/logo_black.png"
                            : `https://cdn.sendcloud.com/global-media/${option.carrier.toLowerCase()}/img/logo.svg`
                        }
                        alt={option.carrier}
                        style={{
                          height: "20px",
                          maxWidth: "50px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <Text strong style={{ fontSize: 13, color: "#1890ff" }}>
                        {option.name}
                      </Text>
                    </div>
                  );
                })()}
              </div>

              {/* Quantity Input for specific shipping options */}
              {getQuantityInfo(selectedOption) && (
                <div style={{ marginBottom: 16, textAlign: "center" }}>
                  <Space direction="vertical" align="center">
                    <Text strong>{getQuantityInfo(selectedOption)?.label}</Text>
                    <InputNumber
                      min={1}
                      max={
                        getQuantityInfo(selectedOption)?.type === "cartons"
                          ? 7
                          : 50
                      }
                      value={quantityInput}
                      onChange={(value) => setQuantityInput(value)}
                      style={{
                        width: 120,
                        borderColor:
                          getQuantityInfo(selectedOption)?.type === "cartons" &&
                          quantityInput &&
                          quantityInput > 7
                            ? "#ff4d4f"
                            : undefined,
                      }}
                    />
                    {getQuantityInfo(selectedOption)?.type === "cartons" &&
                      quantityInput &&
                      quantityInput > 7 && (
                        <Text
                          type="danger"
                          style={{ fontSize: "11px", marginTop: "4px" }}
                        >
                          Maximum 7 cartons allowed
                        </Text>
                      )}
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {getQuantityInfo(selectedOption)?.type === "pallets"
                        ? `Creates one multicollo shipment with ${quantityInput || 0} pallet labels in a single request`
                        : `This will create ${quantityInput || 0} separate labels in Sendcloud`}
                    </Text>
                    {getQuantityInfo(selectedOption)?.type === "cartons" && (
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", fontStyle: "italic" }}
                      >
                        ℹ️{" "}
                        {deliveryCartons
                          ? "Actual cartons from container unloading"
                          : "Sum of all product cartons from booking"}
                      </Text>
                    )}
                    {(selectedOption === 25013 || selectedOption === 53) && (
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", fontStyle: "italic" }}
                      >
                        ℹ️{" "}
                        {deliveryPallets
                          ? "Actual pallets from container unloading"
                          : "Multicollo support: This shipping method can be used for multiple cartons/pallets."}
                      </Text>
                    )}
                  </Space>
                </div>
              )}

              <div style={{ textAlign: "center" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Tooltip
                    title={
                      disabled
                        ? disabledHint ||
                          "Shipment creation is restricted based on booking status. Shipments can only be created after customs clearance."
                        : etaOverrideBlocksShipment &&
                            !isOwnDeliveryMethodId(selectedOption)
                          ? `Customer ETA override ligt na de label-deadline (${getSendcloudCustomerEtaOverrideDeadlineYmd(
                              todayYmdEuropeAmsterdam(),
                            )}, Amsterdam incl. feestdagen): pas de override aan om te verzenden.`
                          : isAmersfoortDisabledAddress()
                            ? "Let op: Dit is een levering naar ons eigen warenhuis."
                            : undefined
                    }
                  >
                    <Button
                      type="primary"
                      size="large"
                      icon={<ShoppingOutlined />}
                      onClick={handleCreateShipment}
                      disabled={
                        disabled ||
                        (etaOverrideBlocksShipment &&
                          !isOwnDeliveryMethodId(selectedOption)) ||
                        isAmersfoortDisabledAddress() ||
                        Boolean(
                          getQuantityInfo(selectedOption) &&
                          quantityInput === null,
                        ) ||
                        Boolean(
                          getQuantityInfo(selectedOption)?.type === "cartons" &&
                          quantityInput &&
                          quantityInput > 7,
                        )
                      }
                      loading={loading}
                      block
                    >
                      Create Shipment with Selected Option
                    </Button>
                  </Tooltip>
                  <Button
                    onClick={() => setSelectedOption(null)}
                    disabled={disabled}
                    block
                  >
                    Clear Selection
                  </Button>
                </Space>
              </div>
            </>
          )}
        </>
      )}

      {/* LVB Warning Modal */}
      <Modal
        open={showLVBWarning}
        title={
          <Space>
            <ExclamationCircleOutlined
              style={{ color: "#faad14", fontSize: 24 }}
            />
            <span style={{ fontSize: 20, fontWeight: "bold" }}>
              Let op: Dit is een levering naar ons eigen warenhuis.
            </span>
          </Space>
        }
        onCancel={() => setShowLVBWarning(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowLVBWarning(false)}>
            Annuleren
          </Button>,
          <Button
            key="proceed"
            type="primary"
            onClick={() => {
              setShowLVBWarning(false);
              createShipmentInternal();
            }}
            loading={loading}
          >
            Verzending Aanmaken
          </Button>,
        ]}
        width={600}
        centered
      >
        <Alert
          message="LVB Levering Gedetecteerd"
          description={
            <div style={{ marginTop: 12 }}>
              <p>Het leveringsadres bevat een van de volgende LVB-adressen:</p>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>Mechie Trommelenweg</li>
                <li>Veerweg</li>
                <li>Divisiedok</li>
              </ul>
              <p style={{ marginTop: 12, fontWeight: "bold" }}>
                Weet u zeker dat u de verzending wilt aanmaken?
              </p>
            </div>
          }
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
        />
      </Modal>
    </Card>
  );
};
